"use client"

import type { ReactNode } from "react"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { useTracking } from "@lib/analytics/tracking-context"
import type { GtmItem } from "@lib/analytics/gtm"

/**
 * A LocalizedClientLink that fires `select_item` before navigating. Used for the
 * ProductCard image slides and the title/price link so a click from any list
 * surface (category rails, /store, search, collections) records the selection.
 * `listId`/`listName` mirror the surrounding ListViewTracker when the surface
 * passes them; they are optional so cards on surfaces without a list still nav.
 */
export default function SelectItemLink({
  href,
  item,
  listId,
  listName,
  onClick,
  children,
  ...rest
}: {
  href: string
  item?: GtmItem
  listId?: string
  listName?: string
  onClick?: () => void
  children?: ReactNode
  [key: string]: unknown
}) {
  const { trackSelectItem } = useTracking()

  const handleClick = () => {
    if (item) {
      // Tracking must never interrupt the click → swallow any failure.
      try {
        trackSelectItem(listId || "", listName || "", item)
      } catch {
        /* noop */
      }
    }
    onClick?.()
  }

  return (
    <LocalizedClientLink href={href} onClick={handleClick} {...rest}>
      {children}
    </LocalizedClientLink>
  )
}
